<template>
  <div class="iframe-mode">
    <div v-if="loading" class="status-mask status-mask--overlay">
      <div class="loading-spinner"></div>
      <div class="status-text">加载中...</div>
    </div>

    <div v-else-if="loadFailed" class="status-mask">
      <div class="error-icon">⚠️</div>
      <div class="status-title">页面无法在此显示</div>
      <div class="status-text">目标网站可能不支持嵌入显示</div>
    </div>

    <iframe
      v-show="!loadFailed"
      ref="iframe"
      :src="url"
      class="iframe-mode-content"
      frameborder="0"
      allowfullscreen
      @load="onIframeLoad"
    ></iframe>
  </div>
</template>

<script>
import * as API_Common from '@/api/common.js'
import { parseAppFrameLinkUrl, ensureAbsoluteHttpUrl } from '@/utils/appFrameUrl.js'

const LOAD_TIMEOUT_MS = 20000
const VERIFY_INTERVAL_MS = 300
const VERIFY_MAX_ATTEMPTS = 20

export default {
  name: 'TgFrame',
  data() {
    return {
      url: '',
      targetUrl: '',
      loading: true,
      loadFailed: false,
      loadSucceeded: false,
      loadTimer: null,
      verifyTimer: null,
      loadTracked: false,
      processId: this.$route.query.processId || '',
      source: this.$route.query.source || '',
    }
  },
  created() {
    window.__TRACK_SCENE_KEY__ = 'tgFrame'
    this.init()
  },
  beforeDestroy() {
    window.__TRACK_SCENE_KEY__ = ''
    this.clearLoadTimer()
    this.clearVerifyTimer()
  },
  methods: {
    trackContext() {
      return { remark: this.url || this.targetUrl }
    },
    markLoadSuccess() {
      if (this.loadSucceeded) return
      this.loadSucceeded = true
      this.clearLoadTimer()
      this.clearVerifyTimer()
      this.loading = false
      this.loadFailed = false
      if (this.loadTracked) return
      this.loadTracked = true
      API_Common.handleBatchTrackBase(100091, this.trackContext())
    },
    markLoadFailed() {
      if (this.loadSucceeded) return
      this.clearLoadTimer()
      this.clearVerifyTimer()
      this.loadFailed = true
      this.loading = false
      if (this.loadTracked) return
      this.loadTracked = true
      API_Common.handleBatchTrackBase(100092, this.trackContext())
    },
    appendThemeParam(rawUrl) {
      const sep = rawUrl.includes('?') ? '&' : '?'
      // fromTheme 为正确拼写；fromeTheme 保留兼容历史
      return `${rawUrl}${sep}fromTheme=tgFrame&fromeTheme=tgFrame`
    },
    init() {
      this.targetUrl = ensureAbsoluteHttpUrl(parseAppFrameLinkUrl(this.$route.query.url || ''))

      if (!this.targetUrl || !/^https?:\/\/.+/i.test(this.targetUrl)) {
        this.markLoadFailed()
        return
      }

      this.url = this.appendThemeParam(this.targetUrl)
      this.loadTimer = setTimeout(() => {
        if (!this.loadSucceeded) {
          this.markLoadFailed()
        }
      }, LOAD_TIMEOUT_MS)
    },
    clearLoadTimer() {
      if (this.loadTimer) {
        clearTimeout(this.loadTimer)
        this.loadTimer = null
      }
    },
    clearVerifyTimer() {
      if (this.verifyTimer) {
        clearTimeout(this.verifyTimer)
        this.verifyTimer = null
      }
    },
    hasIframeContent(iframe) {
      const doc = iframe.contentDocument || iframe.contentWindow?.document
      if (!doc) return false
      const content = (doc.body?.innerHTML || '').trim()
      return content.length >= 10
    },
    onIframeLoad() {
      this.clearVerifyTimer()
      this.verifyIframeContent(0)
    },
    verifyIframeContent(attempt) {
      if (this.loadSucceeded) return

      const iframe = this.$refs.iframe
      if (!iframe) {
        if (attempt >= VERIFY_MAX_ATTEMPTS) return
        this.verifyTimer = setTimeout(() => {
          this.verifyIframeContent(attempt + 1)
        }, VERIFY_INTERVAL_MS)
        return
      }

      try {
        if (this.hasIframeContent(iframe)) {
          this.markLoadSuccess()
          return
        }
      } catch (e) {
        // 跨域无法读 document，说明外部页已加载
        this.markLoadSuccess()
        return
      }

      if (attempt >= VERIFY_MAX_ATTEMPTS) {
        // 可能即将二次跳转，交给绝对超时兜底，不提前展示失败页
        return
      }

      this.verifyTimer = setTimeout(() => {
        this.verifyIframeContent(attempt + 1)
      }, VERIFY_INTERVAL_MS)
    },
  },
}
</script>

<style lang="scss" scoped>
.iframe-mode {
  width: 100%;
  height: 100vh;
  position: relative;

  .iframe-mode-content {
    width: 100%;
    height: 100%;
    border: none;
  }
}

.status-mask {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.4rem;
  box-sizing: border-box;
  background: #fff;
}

.status-mask--overlay {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
}

.loading-spinner {
  width: 0.6rem;
  height: 0.6rem;
  border: 0.04rem solid #f0f0f0;
  border-top-color: #ff6b35;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 0.24rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-icon {
  font-size: 0.8rem;
  margin-bottom: 0.24rem;
}

.status-title {
  font-size: 0.32rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.12rem;
}

.status-text {
  font-size: 0.26rem;
  color: #999;
  margin-bottom: 0.32rem;
}

.primary-btn {
  min-width: 2.4rem;
  height: 0.8rem;
  padding: 0 0.32rem;
  border: none;
  border-radius: 0.4rem;
  background: linear-gradient(135deg, #ff6b35 0%, #ff9e45 100%);
  color: #fff;
  font-size: 0.28rem;
}
</style>
